import type { RefObject } from 'react';
import { useContext, useEffect } from 'react';

import { WidgetAnimationContext } from '@/features/Dashboard/context/WidgetAnimation/context';

export const useWidgetAnimation = () => {
  const ctx = useContext(WidgetAnimationContext);
  if (!ctx) throw new Error('useWidgetAnimation must be used within WidgetAnimationProvider');
  return ctx;
};

export const useScrollToAnimatedElement = (
  ref: RefObject<HTMLElement | null>,
  elementId: string
) => {
  const { elementsToAnimateId, setElementsToAnimateId, animationTime } = useWidgetAnimation();
  const isAnimating = !!elementsToAnimateId?.has(elementId);

  useEffect(() => {
    if (!isAnimating) return;

    const element = ref.current;
    if (!element) return;

    const scrollToElement = () => {
      element.scrollIntoView({ behavior: 'smooth' });
    };

    scrollToElement();

    const resizeObserver = new ResizeObserver(scrollToElement);
    resizeObserver.observe(element);

    const timeoutId = setTimeout(() => {
      setElementsToAnimateId(new Set());
    }, animationTime);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [isAnimating, ref, animationTime, setElementsToAnimateId]);

  return isAnimating;
};
